import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import "../../scss/familyTree/searchBar.scss";

/**
 * SearchBar Component
 * Beautiful search input with autocomplete dropdown for family members
 */
const SearchBar = forwardRef(
  (
    {
      members = [],
      onSelectMember = () => {},
      placeholder = "Search family members...",
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const searchRef = useRef(null);
    const dropdownRef = useRef(null);

    // Expose reset method to parent component
    useImperativeHandle(ref, () => ({
      reset: () => {
        setSearchTerm("");
        setShowDropdown(false);
        setSelectedIndex(-1);
      },
    }));

    // Filter members based on search term
    const filteredMembers = searchTerm.trim()
      ? members.filter((member) => {
          const searchLower = searchTerm.toLowerCase();
          const fullName = `${member.firstName || ""} ${
            member.lastName || ""
          }`.toLowerCase();
          const firstName = (member.firstName || "").toLowerCase();
          const lastName = (member.lastName || "").toLowerCase();

          return (
            fullName.includes(searchLower) ||
            firstName.includes(searchLower) ||
            lastName.includes(searchLower)
          );
        })
      : [];

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (
          searchRef.current &&
          !searchRef.current.contains(e.target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target)
        ) {
          setShowDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle search input change
    const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchTerm(value);
      setShowDropdown(value.trim().length > 0);
      setSelectedIndex(-1);
    };

    // Handle member selection
    const handleSelectMember = (member) => {
      setSearchTerm(`${member.firstName} ${member.lastName}`);
      setShowDropdown(false);
      setSelectedIndex(-1);
      onSelectMember(member);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
      if (!showDropdown || filteredMembers.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredMembers.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && filteredMembers[selectedIndex]) {
            handleSelectMember(filteredMembers[selectedIndex]);
          } else if (filteredMembers.length === 1) {
            handleSelectMember(filteredMembers[0]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowDropdown(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    };

    // Handle clear search
    const handleClear = () => {
      setSearchTerm("");
      setShowDropdown(false);
      setSelectedIndex(-1);
    };

    // Get member generation display
    const getMemberGenerationText = (member) => {
      if (member.generation !== undefined && member.generation !== null) {
        return `Gen ${member.generation}`;
      }
      return "";
    };

    return (
      <div className="search-bar-container">
        <div className="search-bar" ref={searchRef}>
          {/* Search Icon */}
          <svg
            className="search-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>

          {/* Search Input */}
          <input
            type="text"
            className="search-input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchTerm.trim().length > 0) {
                setShowDropdown(true);
              }
            }}
          />

          {/* Clear Button */}
          {searchTerm && (
            <button className="clear-btn" onClick={handleClear} type="button">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Autocomplete Dropdown */}
        {showDropdown && filteredMembers.length > 0 && (
          <div className="search-dropdown" ref={dropdownRef}>
            <div className="dropdown-header">
              <span className="result-count">
                {filteredMembers.length}{" "}
                {filteredMembers.length === 1 ? "member" : "members"} found
              </span>
            </div>
            <ul className="dropdown-list">
              {filteredMembers.map((member, index) => (
                <li
                  key={member.id}
                  className={`dropdown-item ${
                    selectedIndex === index ? "selected" : ""
                  }`}
                  onClick={() => handleSelectMember(member)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="member-info">
                    {/* Member Avatar */}
                    <div className="member-avatar">
                      {member.profileImageUrl ? (
                        <img
                          src={member.profileImageUrl}
                          alt={member.firstName}
                        />
                      ) : (
                        <div className="avatar-placeholder">
                          {(member.firstName?.[0] || "?").toUpperCase()}
                          {(member.lastName?.[0] || "").toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Member Details */}
                    <div className="member-details">
                      <div className="member-name">
                        {member.firstName} {member.lastName}
                      </div>
                      <div className="member-meta">
                        {getMemberGenerationText(member)}
                        {member.dateOfBirth && (
                          <>
                            {getMemberGenerationText(member) && " • "}
                            Born {new Date(member.dateOfBirth).getFullYear()}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Selection Indicator */}
                  {selectedIndex === index && (
                    <svg
                      className="selection-indicator"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* No Results Message */}
        {showDropdown && searchTerm.trim() && filteredMembers.length === 0 && (
          <div className="search-dropdown" ref={dropdownRef}>
            <div className="no-results">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <p>No members found</p>
              <span>Try searching with a different name</span>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default SearchBar;
